// Full-screen replacement for the app when the BE's IP allow-list has
// refused this device (403, code "ip_not_allowed"). Rendered by App on
// NETWORK_BLOCKED_EVENT, signed in or not.
//
// Deliberately explicit rather than vague: the person seeing this is
// almost always a colleague on a home or mobile network, and the one
// thing an admin needs from them is the IP shown here. It is the address
// the BE saw (normalised, so a v4-mapped-v6 reads as plain IPv4), which
// is exactly what the Quick-add button under Settings > Network access
// will offer. An attacker learns nothing from it that the 403 and their
// own address did not already tell them.
//
// The copy button puts the address on the clipboard so it can be pasted
// into a message to an admin. Clipboard access can be refused (older
// WebViews, non-secure contexts), so it falls back to selecting the text.

import { useEffect, useRef, useState } from "react";
import { Check, Copy, ShieldOff } from "lucide-react";
import { useTranslation } from "../i18n";
import LanguageToggle from "./LanguageToggle";
import "../styles/login.css";
import "../styles/network-blocked.css";

interface Props {
    /** The client IP as the BE resolved it; null when it could not be parsed. */
    ip: string | null;
    onRetry: () => void;
}

const COPIED_FEEDBACK_MS = 2000;

export default function NetworkBlocked({ ip, onRetry }: Props) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const ipRef = useRef<HTMLElement>(null);
    const timer = useRef<number | null>(null);

    useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

    const selectIp = (): boolean => {
        const el = ipRef.current;
        if (!el) return false;
        try {
            const range = document.createRange();
            range.selectNodeContents(el);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
            return document.execCommand("copy");
        } catch {
            return false;
        }
    };

    const copyIp = async () => {
        if (!ip) return;
        let ok = false;
        try {
            await navigator.clipboard.writeText(ip);
            ok = true;
        } catch {
            ok = selectIp();
        }
        if (!ok) return;
        setCopied(true);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    };

    return (
        <div id="login-background" className="network-blocked">
            <div id="login-box" className="animate-slideUp" role="alert" aria-live="assertive">
                <LanguageToggle className="login-language-toggle" />

                <ShieldOff size={40} className="network-blocked-icon" aria-hidden />
                <h1 className="login-title">{t("network_blocked_title")}</h1>
                <p className="network-blocked-body">{t("network_blocked_body")}</p>

                <div className="network-blocked-ip-block">
                    <span className="network-blocked-ip-label">{t("network_blocked_ip_label")}</span>
                    <div className="network-blocked-ip-row">
                        <code ref={ipRef} className="network-blocked-ip" data-testid="network-blocked-ip">
                            {ip ?? t("network_blocked_ip_unknown")}
                        </code>
                        {ip && (
                            <button
                                type="button"
                                className={`network-blocked-copy${copied ? " network-blocked-copy--done" : ""}`}
                                onClick={copyIp}
                                aria-label={copied ? t("network_blocked_copied") : t("network_blocked_copy")}
                                title={copied ? t("network_blocked_copied") : t("network_blocked_copy")}
                            >
                                {copied ? <Check size={18} aria-hidden /> : <Copy size={18} aria-hidden />}
                            </button>
                        )}
                    </div>
                    <span className="network-blocked-copied" aria-live="polite">
                        {copied ? t("network_blocked_copied") : ""}
                    </span>
                </div>

                <p className="network-blocked-body network-blocked-hint">{t("network_blocked_hint")}</p>

                <button type="button" className="btn btn-primary btn-full" onClick={onRetry}>
                    {t("network_blocked_retry")}
                </button>
            </div>
        </div>
    );
}
